import { useEffect, useState, useCallback, useRef } from 'react';
import './CategoryPage.css';
import { supabase } from '../supabaseClient';
import RecipeCard from './RecipeCard';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import { mergeRecipeTitles } from '../utils/fetchTranslatedRecipes';

// Wrapper that triggers staggered scroll-in animation on its children
function AnimatedGrid({ children }) {
  const ref = useRef(null);

  useEffect(() => {
    const grid = ref.current;
    if (!grid) return;
    const items = grid.querySelectorAll('.animate-in');
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.06 }
    );
    items.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [children]);

  return (
    <div ref={ref} className="category-grid">
      {children}
    </div>
  );
}

// SVG icons for categories with no recipe image
const CATEGORY_ICONS = {
  'main-course': (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M8 12h8M12 8v8"/>
    </svg>
  ),
  'soup': (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 11h16v2a8 8 0 0 1-16 0v-2z"/>
      <path d="M8 11V7m4 4V5m4 6V7"/>
    </svg>
  ),
  'dessert': (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2c0 0-4 2-4 6h8c0-4-4-6-4-6z"/>
      <rect x="5" y="8" width="14" height="3" rx="1"/>
      <path d="M6 11v7a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-7"/>
    </svg>
  ),
  'baking': (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 19h14M5 19a2 2 0 0 1-2-2v-1h18v1a2 2 0 0 1-2 2M3 16V9a9 9 0 0 1 18 0v7"/>
    </svg>
  ),
  'breakfast': (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
      <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
      <line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/>
    </svg>
  ),
  'starters': (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l19-9-9 19-2-8-8-2z"/>
    </svg>
  ),
  'side-dishes': (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"/>
      <path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
      <path d="M12 4v2m0 12v2M4 12h2m12 0h2"/>
    </svg>
  ),
};

const TILE_ICON_ALL = (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);

const TILE_ICON_FAV = (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const TILE_ICON_COLLECTION = (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
  </svg>
);

const LS_KEY = 'solt_pantry_swipe_saved';

function CategoryPage({ onSelectCategory, onSelectRecipe, onShowAllRecipes, onShowFavourites, onSelectCollection, onShowSwipe, onShowVisitorPicks }) {
  const { language } = useLanguage();
  const t = translations[language];
  const { user, isAdmin, isMember } = useAuth();
  const [categories, setCategories] = useState([]);
  const [recipeMeta, setRecipeMeta] = useState({});
  const [allRecipeImages, setAllRecipeImages] = useState([]);
  const [totalRecipeCount, setTotalRecipeCount] = useState(0);
  const [, setFavouriteCount] = useState(0);
  const [collections, setCollections] = useState([]);
  const [collectionCounts, setCollectionCounts] = useState({});
  const [collectionImages, setCollectionImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [visitorPickCount, setVisitorPickCount] = useState(0);
  const [visitorSavedSet, setVisitorSavedSet] = useState(() =>
    new Set(JSON.parse(localStorage.getItem(LS_KEY) || '[]'))
  );

  // For visitors: read saved count from localStorage and keep in sync
  useEffect(() => {
    if (!user) {
      const refresh = () => {
        const stored = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
        setVisitorPickCount(stored.length);
        setVisitorSavedSet(new Set(stored));
      };
      refresh();
      window.addEventListener('visitorPicksChanged', refresh);
      return () => window.removeEventListener('visitorPicksChanged', refresh);
    }
  }, [user]);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const { data: cats, error: catsError } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order', { ascending: true });

      if (catsError) {
        console.error('Error fetching categories:', catsError);
        setLoading(false);
        return;
      }

      const { data: recipes, error: recipesError } = await supabase
        .from('recipes')
        .select('category, image_url, is_favourite');

      if (recipesError) {
        console.error('Error fetching recipes:', recipesError);
      }

      const meta = {};
      let favCount = 0;
      if (recipes) {
        for (const r of recipes) {
          const cat = r.category;
          if (!cat) continue;
          if (!meta[cat]) meta[cat] = { count: 0, image_url: null, image_url2: null };
          meta[cat].count++;
          if (!meta[cat].image_url && r.image_url) meta[cat].image_url = r.image_url;
          else if (!meta[cat].image_url2 && r.image_url && r.image_url !== meta[cat].image_url) meta[cat].image_url2 = r.image_url;
          if (r.is_favourite) favCount++;

        }
      }

      // For members, override favCount with their own user_favourites count
      if (isMember && user) {
        const { count } = await supabase
          .from('user_favourites')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        favCount = count || 0;
      }

      // Collections are private — only fetch for admin/member, never for visitors
      let cols = [];
      const counts = {};
      if (isAdmin || isMember) {
        let colQuery = supabase.from('collections').select('*').order('created_at', { ascending: true });
        if (isAdmin) colQuery = colQuery.is('user_id', null);
        else colQuery = colQuery.eq('user_id', user?.id);
        const { data: colData } = await colQuery;
        cols = colData || [];

        if (cols.length > 0) {
          const { data: colRecipes } = await supabase
            .from('collection_recipes')
            .select('collection_id, recipes(image_url)');
          const colImgs = {};
          if (colRecipes) {
            for (const row of colRecipes) {
              counts[row.collection_id] = (counts[row.collection_id] || 0) + 1;
              if (!colImgs[row.collection_id] && row.recipes?.image_url) {
                colImgs[row.collection_id] = row.recipes.image_url;
              }
            }
          }
          setCollectionImages(colImgs);
        }
      }

      const imgs = recipes ? [...new Set(recipes.filter(r => r.image_url).map(r => r.image_url))].slice(0, 4) : [];
      setCategories(cats);
      setRecipeMeta(meta);
      setAllRecipeImages(imgs);
      setTotalRecipeCount(recipes ? recipes.length : 0);
      setFavouriteCount(favCount);
      setCollections(cols);
      setCollectionCounts(counts);
      setLoading(false);
    }

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isMember, user?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const runSearch = useCallback(async (q) => {
    if (!q) {
      setSearchResults([]);
      return;
    }
    setSearching(true);

    const { data: byTitle } = await supabase
      .from('recipes')
      .select('*')
      .ilike('title', `%${q}%`);

    const { data: byIngredient } = await supabase
      .from('recipe_ingredients')
      .select('recipe_id')
      .ilike('ingredient_name', `%${q}%`);

    let results = byTitle || [];
    const seenIds = new Set(results.map(r => r.id));

    if (byIngredient && byIngredient.length > 0) {
      const ingredientIds = [...new Set(byIngredient.map(row => row.recipe_id))];
      const idsToFetch = ingredientIds.filter(id => !seenIds.has(id));
      if (idsToFetch.length > 0) {
        const { data: byIngredientRecipes } = await supabase
          .from('recipes')
          .select('*')
          .in('id', idsToFetch);
        if (byIngredientRecipes) {
          byIngredientRecipes.forEach(r => seenIds.add(r.id));
          results = [...results, ...byIngredientRecipes];
        }
      }
    }

    if (language === 'hu') {
      const { data: byHuTitle } = await supabase
        .from('recipe_translations')
        .select('recipe_id')
        .eq('language_code', 'hu')
        .ilike('title', `%${q}%`);

      if (byHuTitle && byHuTitle.length > 0) {
        const huIds = byHuTitle.map(row => row.recipe_id).filter(id => !seenIds.has(id));
        if (huIds.length > 0) {
          const { data: byHuRecipes } = await supabase
            .from('recipes')
            .select('*')
            .in('id', huIds);
          if (byHuRecipes) results = [...results, ...byHuRecipes];
        }
      }
    }

    const merged = await mergeRecipeTitles(results, language);
    setSearchResults(merged);
    setSearching(false);
  }, [language]);

  useEffect(() => {
    runSearch(debouncedQuery);
  }, [debouncedQuery, runSearch]);

  const handleClearSearch = () => {
    setQuery('');
    setDebouncedQuery('');
    setSearchResults([]);
  };

  function handleVisitorToggle(id, val) {
    const stored = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    const next = val ? [...new Set([...stored, id])] : stored.filter(x => x !== id);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    setVisitorPickCount(next.length);
    setVisitorSavedSet(new Set(next));
    window.dispatchEvent(new CustomEvent('visitorPicksChanged'));
  }

  if (loading) {
    return <p style={{ color: '#999', fontStyle: 'italic', padding: '32px 0' }}>{t.loading}</p>;
  }

  const isSearching = debouncedQuery.length > 0;

  return (
    <main className="category-page">

      <div className={`category-hero${isSearching ? ' category-hero--searching' : ''}`}>
        {!isSearching && (
          <div className="category-hero__tagline-row">
            <p className="category-hero__tagline">
              {language === 'hu' ? 'Ma mit főzzünk?' : 'What shall we cook today?'}
            </p>
          </div>
        )}
        <div className="category-hero__search-row">
          <div className="search-bar category-hero__search-bar">
          <input
            className="search-bar__input"
            type="text"
            placeholder={t.searchPlaceholder}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query && (
            <button className="search-bar__clear" onClick={handleClearSearch} aria-label="Clear search">
              ✕
            </button>
          )}
        </div>
        <button className="hero-swipe-btn" onClick={onShowSwipe} aria-label={language === 'hu' ? 'Receptek felfedezése' : 'Discover recipes'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 3 21 3 21 8"/>
            <line x1="4" y1="20" x2="21" y2="3"/>
            <polyline points="21 16 21 21 16 21"/>
            <line x1="15" y1="15" x2="21" y2="21"/>
          </svg>
          <span>{language === 'hu' ? 'Felfedezés' : 'Discover'}</span>
        </button>
        </div>
      </div>

      {isSearching ? (
        <div className="search-results">
          <p className="search-results__header">
            {searching
              ? t.searching
              : searchResults.length === 0
                ? t.noRecipesFound(debouncedQuery)
                : searchResults.length === 1
                  ? t.oneRecipeFound(debouncedQuery)
                  : t.recipesFound(searchResults.length, debouncedQuery)
            }
          </p>
          {!searching && searchResults.length > 0 && (
            <div className="search-results__grid">
              {searchResults.map(recipe => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onClick={() => onSelectRecipe(recipe.id)}
                  isAdmin={isAdmin || isMember}
                  showDraft={isAdmin}
                  visitorLiked={!user ? visitorSavedSet.has(recipe.id) : undefined}
                  onToggleFavourite={(id, val) => {
                    if (!user) {
                      handleVisitorToggle(id, val);
                      return;
                    }
                    if (isMember && user) {
                      if (val) supabase.from('user_favourites').insert({ user_id: user.id, recipe_id: id });
                      else supabase.from('user_favourites').delete().eq('user_id', user.id).eq('recipe_id', id);
                    } else {
                      supabase.from('recipes').update({ is_favourite: val }).eq('id', id);
                    }
                    setSearchResults(prev => prev.map(r => r.id === id ? { ...r, is_favourite: val } : r));
                  }}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <AnimatedGrid>
          {/* My Picks — visitors only, shown FIRST if they have any saved */}
          {!user && visitorPickCount > 0 && (
            <div
              className="category-tile category-tile--favourites animate-in"
              style={{ animationDelay: '0s' }}
              onClick={onShowVisitorPicks}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && onShowVisitorPicks && onShowVisitorPicks()}
            >
              <div className="category-tile__image">
                <div className="category-tile__fav-bg" />
                <div className="category-tile__overlay" />
                <div className="category-tile__text">
                  <span className="category-tile__name">{t.myPicks}</span>
                </div>
              </div>
            </div>
          )}

          {/* All Recipes */}
          {(() => {
            const delay = `${(!user && visitorPickCount > 0 ? 1 : 0) * 0.08}s`;
            const allImages = allRecipeImages;
            return (
              <div
                className="category-tile category-tile--all animate-in"
                style={{ animationDelay: delay }}
                onClick={onShowAllRecipes}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && onShowAllRecipes()}
              >
                <div className="category-tile__image">
                  {allImages.length >= 2 && (
                    <div className="category-tile__quad">
                      {allImages.map((src, i) => <img key={i} src={src} alt="" />)}
                      {allImages.length < 4 && Array.from({length: 4 - allImages.length}).map((_, i) => <div key={`empty-${i}`} className="category-tile__quad-empty" />)}
                    </div>
                  )}
                  <div className="category-tile__overlay" />
                  <div className="category-tile__text">
                    <span className="category-tile__name">{t.allRecipes}</span>
                    <span className="category-tile__count">
                      {totalRecipeCount === 0 ? t.noRecipesYet : totalRecipeCount === 1 ? t.oneRecipe : t.nRecipes(totalRecipeCount)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Favourites — admin/member only */}
          {(isAdmin || isMember) && (
            <div
              className="category-tile category-tile--favourites animate-in"
              style={{ animationDelay: '0.08s' }}
              onClick={onShowFavourites}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && onShowFavourites()}
            >
              <div className="category-tile__image">
                <div className="category-tile__fav-bg" />
                <div className="category-tile__overlay" />
                <div className="category-tile__text">
                  <span className="category-tile__name">{t.favourites}</span>
                </div>
              </div>
            </div>
          )}

          {collections.map((col, index) => {
            const count = collectionCounts[col.id] || 0;
            const colImage = collectionImages[col.id];
            const baseIndex = 1 + (isAdmin || isMember ? 1 : 0) + (!user && visitorPickCount > 0 ? 1 : 0) + index;
            const delay = `${baseIndex * 0.08}s`;
            return (
              <div
                key={`col-${col.id}`}
                className="category-tile category-tile--collection animate-in"
                style={{ animationDelay: delay }}
                onClick={() => onSelectCollection && onSelectCollection(col)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && onSelectCollection && onSelectCollection(col)}
              >
                <div className="category-tile__image">
                  {colImage
                    ? <img src={colImage} alt={col.name} />
                    : <div className="category-tile__collection-bg" />
                  }
                  <div className="category-tile__overlay" />
                  <div className="category-tile__text">
                    <span className="category-tile__name">{(language === 'hu' && col.name_hu) ? col.name_hu : col.name}</span>
                    <span className="category-tile__count">
                      {count === 0 ? t.noRecipesYet : count === 1 ? t.oneRecipe : t.nRecipes(count)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {categories.map((cat, index) => {
            const meta = recipeMeta[cat.name] || { count: 0, image_url: null };
            const image = meta.image_url;
            const icon = CATEGORY_ICONS[cat.slug] || TILE_ICON_ALL;
            const count = meta.count;
            const colOffset = 1 + (isAdmin || isMember ? 1 : 0) + (!user && visitorPickCount > 0 ? 1 : 0) + collections.length; // All Recipes + Favourites/MyPicks + collections
            const delay = `${(colOffset + index) * 0.08}s`;

            return (
              <div
                key={cat.id}
                className="category-tile animate-in"
                style={{ animationDelay: delay }}
                onClick={() => onSelectCategory(cat)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && onSelectCategory(cat)}
              >
                <div className="category-tile__image">
                  {image && <img src={image} alt={cat.name} />}
                  <div className="category-tile__overlay" />
                  <div className="category-tile__text">
                    <span className="category-tile__name">{t.categories[cat.name] || cat.name}</span>
                    <span className="category-tile__count">
                      {count === 0 ? t.noRecipesYet : count === 1 ? t.oneRecipe : t.nRecipes(count)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </AnimatedGrid>
      )}
    </main>
  );
}

export default CategoryPage;
